import React, { useEffect, useState } from "react";
import { Theme, useMediaQuery } from "@mui/material";
import { useThrottledCallback } from "use-debounce";
import { FundRecommendationResponse } from "@rentablo/api/recommender";
import { LoadingPlaceHolder, Modal } from "@rentablo/components";
import { InfoView } from "./InfoView/InfoView";
import { CompareView } from "./CompareView/CompareView";
import { useRecommendationDetails } from "./RecommendationDetailsProvider";

type Props = {
  onClose: () => void;
  onToggleWatchlist: (rec: FundRecommendationResponse) => void;
  isWatchlisted: boolean;
  isSavingWatchlist: boolean;
  initialView?: "info" | "compare" | "onlyCompare";
};

export const DetailsModal = ({
  onClose,
  onToggleWatchlist,
  isWatchlisted,
  isSavingWatchlist,
  initialView = "info",
}: Props) => {
  const [view, setView] = useState<"info" | "compare" | "onlyCompare">(
    initialView
  );
  const { isLoading } = useRecommendationDetails();
  const isLessSm = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );
  const [modalWidth, setModalWidth] = React.useState(860);

  const onResize = useThrottledCallback(() => {
    const width = window
      .getComputedStyle(document.body)
      .getPropertyValue("width");
    const windowWidth = parseInt(width);

    if (isLessSm) {
      setModalWidth(windowWidth);
      return;
    }

    let newModalWidth = (windowWidth - 260) / 2 + 100;
    if (windowWidth >= 624 && newModalWidth < 600) {
      newModalWidth = 600;
    } else if (windowWidth < 624) {
      newModalWidth = windowWidth - 24;
    }

    setModalWidth(newModalWidth);
  }, 200);

  // calculting modal width
  useEffect(() => {
    window.addEventListener("resize", onResize);
    onResize();

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, [isLessSm, onResize]);

  return (
    <Modal
      isOpen
      onClose={onClose}
      width={modalWidth}
      height={isLessSm ? "100%" : "calc(100% - 24px)"}
      position={isLessSm ? "bottom" : "right"}
    >
      {isLoading ? (
        <LoadingPlaceHolder height="100%" />
      ) : view === "info" ? (
        <InfoView
          onClose={onClose}
          onToggleWatchlist={onToggleWatchlist}
          isWatchlisted={isWatchlisted}
          isSavingWatchlist={isSavingWatchlist}
          onCompareClick={() => setView("compare")}
        />
      ) : (
        <CompareView
          onClose={() => (view === "onlyCompare" ? onClose() : setView("info"))}
        />
      )}
    </Modal>
  );
};
